import { useApolloClient } from "@apollo/client";
import { omit, omitBy, isUndefined } from "lodash-es";
import React, { useEffect, useRef } from "react";
import { Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { OrganisationWebsitesDetailsBox } from "../../features/organisations/boxes/OrganisationWebsitesDetailsBox.js";
import {
  useOrganisationWebsitesQuery,
  useOrganisationWebsites_AddProfileMutation,
} from "./OrganisationWebsites.generated.js";
import { LoadingSpinner } from "@msys/ui";

interface Props {
  submenuItems: PageTopbarItem[];
}

const convertToSlug = (title: string): string => {
  let str = title.replace(/^\s+|\s+$/g, "").toLowerCase();

  // remove accents, swap ñ for n, etc
  const from = "åàáãäâèéëêìíïîòóöôùúüûñç·/_,:;";
  const to = "aaaaaaeeeeiiiioooouuuunc------";

  for (let i = 0; i < from.length; i++) {
    str = str.replace(new RegExp(from.charAt(i), "g"), to.charAt(i));
  }

  return str
    .replace(/[^a-z0-9 \-_]/g, "") // remove invalid chars
    .replace(/\s+/g, "-") // collapse whitespace and replace by -
    .replace(/-+/g, "-"); // collapse dashes
};

export function OrganisationWebsites({ submenuItems }: Props) {
  const { t } = useTranslate(["OrganisationPageTopbar", "OrganisationProfile"]);
  const client = useApolloClient();
  const autoCreateAttempted = useRef(false);

  const query = useOrganisationWebsitesQuery({ client });
  const organisation = query.data?.viewer?.organisation;

  const [addProfile, { loading: addProfileLoading }] =
    useOrganisationWebsites_AddProfileMutation({ client });

  // auto-create profile if capability is enabled but no profile exists yet
  useEffect(() => {
    if (
      !autoCreateAttempted.current &&
      organisation &&
      !organisation.profile &&
      !query.loading
    ) {
      autoCreateAttempted.current = true;
      const slug = convertToSlug(organisation.title);
      const optionalFields = omitBy(
        {
          contactWebsite: organisation.website ?? undefined,
          contactEmail: organisation.email ?? undefined,
          contactAddress: organisation.branchAddress
            ? omit(organisation.branchAddress, "__typename", "id")
            : organisation.billingAddress
              ? omit(organisation.billingAddress, "__typename", "id")
              : undefined,
          contactPhones:
            organisation.phones
              .filter(p => p.number.trim())
              .map(p => omit(p, "__typename", "id")) ?? undefined,
          categories:
            organisation.categories && organisation.categories.length > 0
              ? organisation.categories
              : undefined,
        },
        isUndefined
      );
      const input = { slug, ...optionalFields };
      addProfile({ variables: { input } }).catch(() => {
        autoCreateAttempted.current = false;
      });
    }
  }, [organisation, query.loading, addProfile]);

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("Integrations", { ns: "OrganisationPageTopbar" }),
      to: "/organisation/integrations",
    },
    {
      title: t("Websites", { ns: "OrganisationPageTopbar" }),
      to: "/organisation/integrations/websites",
    },
  ];

  const profile = organisation?.profile ?? null;

  return (
    <Page
      title={t("Websites", { ns: "OrganisationPageTopbar" })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      breadcrumbsSize="medium"
    >
      {organisation && profile && (
        <PageContainer>
          <Stack sx={{ width: "100%", maxWidth: 640 }}>
            <OrganisationWebsitesDetailsBox
              organisation={organisation}
              profile={profile}
            />
          </Stack>
        </PageContainer>
      )}
      {organisation && !profile && (query.loading || addProfileLoading) && (
        <PageContainer>
          <LoadingSpinner />
        </PageContainer>
      )}
    </Page>
  );
}
